import React, { useEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import TimeSlotSelection from './pages/time-slot-selection';
import DateSelection from './pages/date-selection';
import AvailabilityManagement from './pages/availability-management';
import BookingsManagement from './pages/bookings-management';
import OrderManagement from './pages/orders-management';
import ServicesManagement from './pages/services-management';
import ClientDetailsForm from './pages/client-details-form';
import BookingConfirmation from './pages/booking-confirmation';
import CatalogDisplay from './pages/catalog-display';
import DiscoveryPage from './pages/discovery';
import AdminAuth from './pages/admin-auth';
import Settings from './pages/settings';
import CartModal from './components/cart/CartModal';
import { CartProvider } from './components/cart/CartContext';
import { ServiceProvider } from './components/context/ServiceContext';
import { BookingProvider } from './components/context/BookingContext';
import { AuthProvider,useAuth } from './components/context/AuthContext';
import { CompanyProvider } from './components/context/CompanyContext';
import ProtectedRoute from './components/ProtectedRoute';
import apiClient from './services/ApiClient';
import { ThemeProvider } from './components/context/ThemeContext';
import AuthenticatedThemeSync from './components/context/AuthenticatedThemeSync';
import PushNotificationLifecycle from './components/context/PushNotificationLifecycle';
import { NotificationProvider } from './components/context/NotificationContext';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { ToastProvider } from "components/context/ToastContext";
import OfflineBanner from './components/ui/OfflineBanner';
import ResetPassword from "./pages/reset-password";
import BookingPaymentCallback from './pages/booking-payment-callback';
import OrderPaymentCallback from './pages/order-payment-callback';
import SubscriptionCallback from './pages/subscription-callback';
import RequestTrackingPage from './pages/request-tracking';
import StaffManagement from './pages/staff-management';
import AcceptInvite from './pages/accept-invite';
import WorkflowManagement from './pages/workflow-management';
import MyTasks from './pages/my-tasks';
import RequestPaymentCallback from './pages/request-payment-callback';
import TermsOfService from './pages/legal/TermsOfService';
import DataUseNotice from './pages/legal/DataUseNotice';

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        {/* AuthProvider must wrap everything that needs auth */}
         <ThemeProvider>
        <AuthProvider>
          <AuthSetup />
          <AuthenticatedThemeSync />
          <PushNotificationLifecycle />
          <CompanyProvider>
          <CartProvider>
            <ServiceProvider>
              <BookingProvider>
                <NotificationProvider>
                <ToastProvider>
                  <RouterRoutes>
                    {/* Public Routes */}
                    <Route path="/:companyPath" >
                    <Route index element={<CatalogDisplay />} />
                    <Route path="discover" element={<DiscoveryPage />} />
                    <Route path="date-selection" element={<DateSelection />} />
                    <Route path="time-slot-selection" element={<TimeSlotSelection />} />
                    <Route path="client-details-form" element={<ClientDetailsForm />} />
                    <Route path="booking-confirmation" element={<BookingConfirmation />} />
                    <Route path="booking-callback" element={<BookingPaymentCallback />} />
                    <Route path="order-callback" element={<OrderPaymentCallback />} />
                    <Route path="request-callback" element={<RequestPaymentCallback />} />
                  </Route>
                  <Route path="/discover" element={<DiscoveryPage />} />
                  <Route path="/track" element={<RequestTrackingPage />} />
                  <Route path="/track/:referenceCode" element={<RequestTrackingPage />} />
                  <Route path="/request-callback" element={<RequestPaymentCallback />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/privacy-policy" element={<DataUseNotice />} />
                  
                  <Route path="/admin/auth" element={<AdminAuth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/accept-invite" element={<AcceptInvite />} />
                  <Route path="/subscription/callback" element={<SubscriptionCallback />} />

                  
                  {/* Protected Admin Routes */}
                  <Route 
                    path="/availability-management" 
                    element={
                      <ProtectedRoute requiredRole="Admin">
                        <AvailabilityManagement />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/bookings-management" 
                    element={
                      <ProtectedRoute requiredRole={['Admin', 'Staff']}>
                        <BookingsManagement />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/order-management" 
                    element={
                      <ProtectedRoute requiredRole={['Admin', 'Staff']}>
                        <OrderManagement />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/services-management" 
                    element={
                      <ProtectedRoute requiredRole="Admin">
                        <ServicesManagement />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/my-tasks"
                    element={
                      <ProtectedRoute>
                        <MyTasks />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/settings" 
                    element={
                      <ProtectedRoute requiredRole="Admin">
                        <Settings />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/staff-management"
                    element={
                      <ProtectedRoute requiredRole="Admin">
                        <StaffManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/workflow-management"
                    element={
                      <ProtectedRoute requiredRole="Admin">
                        <WorkflowManagement />
                      </ProtectedRoute>
                    }
                  />
                      <Route path="/" element={<CatalogDisplay />} />
                  
                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </RouterRoutes>
                <CartModal />
                <OfflineBanner />
                </ToastProvider>
                </NotificationProvider>
              </BookingProvider>
            </ServiceProvider>
          </CartProvider>
          </CompanyProvider>
        </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

// Component to set up the API client refresh callback
// This must be inside the AuthProvider to have access to useAuth
const AuthSetup = () => {
  const { refreshAccessToken } = useAuth();

  useEffect(() => {
    // Set the refresh callback in the API client
    apiClient.setRefreshCallback(refreshAccessToken);
  }, [refreshAccessToken]);

  return null;
};

export default Routes;
